import { useState, useEffect } from 'react';
import { FormSchema, Question as FormQuestion, FormSubmission, FormSchemaCreateRequest as FormData, SchemaGenerateRequest } from '../types';
import { adminFormsService } from '../services/admin-forms.service';

export const useAdminForms = () => {
  const [schemas, setSchemas] = useState<FormSchema[]>([]);
  const [questions, setQuestions] = useState<FormQuestion[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [schemasData, questionsData, submissionsData] = await Promise.all([
        adminFormsService.getAllSchemas(),
        adminFormsService.getAllQuestions(),
        adminFormsService.getAllSubmissions(),
      ]);
      setSchemas(schemasData);
      setQuestions(questionsData);
      setSubmissions(submissionsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load forms data');
    } finally {
      setIsLoading(false);
    }
  };

  const createSchema = async (schemaData: FormData) => {
    const result = await adminFormsService.createSchema(schemaData);
    if (result) await loadData();
    return result;
  };

  const updateSchema = async (schemaId: number, schemaData: Partial<FormData>) => {
    const result = await adminFormsService.updateSchema(schemaId, schemaData);
    if (result) await loadData();
    return result;
  };

  const generateSchema = async (generateData: SchemaGenerateRequest) => {
    const result = await adminFormsService.generateSchema(generateData);
    if (result) await loadData();
    return result;
  };

  const enrichSubmission = async (submissionId: number) => {
    const success = await adminFormsService.enrichSubmission(submissionId);
    if (success) await loadData();
    return success;
  };

  useEffect(() => {
    loadData();
  }, []);

  return {
    schemas,
    questions,
    submissions,
    isLoading,
    error,
    loadData,
    createSchema,
    updateSchema,
    generateSchema,
    enrichSubmission,
  };
};

